import { useState } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import BillingTermToggle from '../../../components/feature/BillingTermToggle';
import {
  formatPrice,
  termImplementation,
  termMonthlyEquivalent,
  termSavings,
  termTotal,
  type BillingTerm,
} from '../../../hooks/useCurrency';
import DentalFeaturesModal from './DentalFeaturesModal';
import {
  dentalCapacityMeta,
  dentalCapacityRules,
  dentalPlans,
  type DentalCurrency,
  type DentalPlan,
} from '../../../mocks/dental';

interface DentalPricingProps {
  currency: DentalCurrency;
  onCurrencyChange: (next: DentalCurrency) => void;
  onSelectPlan: (plan: DentalPlan) => void;
}

const CURRENCIES: { id: DentalCurrency; label: string; aria: string }[] = [
  { id: 'USD', label: 'USD', aria: 'Ver precios en dólares estadounidenses' },
  { id: 'DOP', label: 'RD$', aria: 'Ver precios en pesos dominicanos' },
];

/* La página de Odontología no pasa por i18n: los textos van en español. */
const TERM_LABELS: Record<BillingTerm, string> = {
  monthly: 'Mensual',
  semiannual: 'Semestral',
  annual: 'Anual',
};

const TERM_BADGES = { semiannual: '−10%', annual: '2 meses gratis' };

/** Sufijo del precio y nombre del periodo, por término. */
const TERM_TEXT: Record<BillingTerm, { unit: string; noun: string }> = {
  monthly: { unit: 'por mes', noun: 'mes' },
  semiannual: { unit: 'por semestre', noun: 'semestre' },
  annual: { unit: 'por año', noun: 'año' },
};

export default function DentalPricing({
  currency,
  onCurrencyChange,
  onSelectPlan,
}: DentalPricingProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const accentText = isLight ? '#0f766e' : '#14b8a6';
  const [modalPlan, setModalPlan] = useState<DentalPlan | null>(null);
  const [term, setTerm] = useState<BillingTerm>('monthly');

  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.55)';
  const border = isLight ? 'rgba(10,22,40,0.09)' : 'rgba(255,255,255,0.07)';

  return (
    <section
      id="planes-odontologia"
      className="py-14 md:py-20 px-4 sm:px-6 scroll-mt-24"
      style={{
        background: isLight ? '#f6fafd' : '#04101f',
        borderTop: `1px solid ${border}`,
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* ── Encabezado ── */}
        <div className="text-center mb-8 md:mb-10">
          <span
            className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2.5"
            style={{ color: isLight ? '#0e7490' : '#22d3ee' }}
          >
            Planes de Odontología
          </span>
          <h2
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3"
            style={{ color: ink }}
          >
            Un plan para cada <span style={{ color: accentText }}>etapa de tu clínica</span>
          </h2>
          <p className="font-body text-sm md:text-base max-w-2xl mx-auto mb-6" style={{ color: sub }}>
            Los planes se diferencian por funciones, no solo por capacidad. La implementación es un
            pago único y se cobra aparte de la mensualidad.
          </p>

          {/* Selector de moneda */}
          <div className="mb-4"><div
            className="inline-flex items-center gap-1 rounded-full px-1 py-1 border"
            role="group"
            aria-label="Seleccionar moneda de los precios"
            style={{
              backgroundColor: isLight ? '#e6eef6' : 'rgba(7,27,46,0.85)',
              borderColor: isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.10)',
            }}
          >
            {CURRENCIES.map((c) => {
              const active = currency === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onCurrencyChange(c.id)}
                  aria-pressed={active}
                  aria-label={c.aria}
                  className="px-5 py-2 rounded-full text-sm font-bold font-body transition-all duration-200 cursor-pointer whitespace-nowrap touch-manipulation"
                  style={{
                    background: active ? 'linear-gradient(135deg, #06b6d4, #14b8a6)' : 'transparent',
                    color: active
                      ? '#04101f'
                      : isLight
                        ? 'rgba(10,22,40,0.80)'
                        : 'rgba(255,255,255,0.70)',
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div></div>

          {/* Selector de forma de pago */}
          <div className="flex flex-col items-center gap-2">
            <BillingTermToggle
              term={term}
              onChange={setTerm}
              labels={TERM_LABELS}
              badges={TERM_BADGES}
              activeBackground="linear-gradient(135deg, #06b6d4, #14b8a6)"
              activeColor="#04101f"
            />
            <span className="font-body text-[11px]" style={{ color: sub }}>
              Descuento permanente: el precio no sube en la renovación.
            </span>
          </div>
        </div>

        {/* ── Tarjetas ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6 items-start">
          {dentalPlans.map((plan) => (
            <DentalPlanCard
              key={plan.id}
              plan={plan}
              currency={currency}
              term={term}
              isLight={isLight}
              onSelectPlan={onSelectPlan}
              onShowAll={() => setModalPlan(plan)}
            />
          ))}
        </div>

        {/* ── Reglas de capacidad ── */}
        <div
          className="mt-10 md:mt-12 rounded-2xl border px-5 py-6 md:px-8 md:py-7 max-w-5xl mx-auto"
          style={{
            borderColor: border,
            background: isLight ? '#ffffff' : 'rgba(7,27,46,0.55)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-information-line text-base" style={{ color: '#14b8a6' }} aria-hidden="true" />
            <h3
              className="font-heading text-sm font-bold uppercase tracking-wider"
              style={{ color: isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.68)' }}
            >
              Cómo se cuenta la capacidad
            </h3>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {dentalCapacityRules.map((rule) => (
              <li
                key={rule.text}
                className="flex items-start gap-2.5 font-body text-xs md:text-[13px] leading-relaxed"
                style={{ color: sub }}
              >
                <i
                  className={`${rule.icon} flex-shrink-0 mt-[2px] text-sm`}
                  style={{ color: '#06b6d4' }}
                  aria-hidden="true"
                />
                <span>{rule.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {modalPlan && (
        <DentalFeaturesModal
          plan={modalPlan}
          currency={currency}
          onClose={() => setModalPlan(null)}
        />
      )}
    </section>
  );
}

/* ─── Tarjeta de plan ──────────────────────────────────────────────────── */

interface DentalPlanCardProps {
  plan: DentalPlan;
  currency: DentalCurrency;
  term: BillingTerm;
  isLight: boolean;
  onSelectPlan: (plan: DentalPlan) => void;
  onShowAll: () => void;
}

function DentalPlanCard({
  plan,
  currency,
  term,
  isLight,
  onSelectPlan,
  onShowAll,
}: DentalPlanCardProps) {
  const [hovered, setHovered] = useState(false);
  const accent = plan.accent;
  const recommended = Boolean(plan.badge);

  const base = plan.monthly[currency];
  const price = formatPrice(termTotal(base, term), currency);
  const savings = termSavings(base, term);
  const implFull = plan.implementation[currency];
  const implNow = termImplementation(implFull, term);
  const implDiscounted = implNow < implFull;
  const termText = TERM_TEXT[term];

  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.70)' : 'rgba(255,255,255,0.62)';

  const cardBg = isLight
    ? recommended
      ? `linear-gradient(165deg, ${accent}14 0%, #ffffff 55%)`
      : '#ffffff'
    : recommended
      ? `linear-gradient(165deg, ${accent}24 0%, rgba(7,27,46,0.98) 60%)`
      : 'rgba(7,27,46,0.92)';

  const borderColor = recommended
    ? accent + (isLight ? 'aa' : '85')
    : hovered
      ? accent + (isLight ? '77' : '55')
      : isLight
        ? 'rgba(10,22,40,0.10)'
        : 'rgba(255,255,255,0.08)';

  const shadow = isLight
    ? recommended || hovered
      ? `0 12px 36px ${accent}26, 0 4px 14px rgba(10,22,40,0.07)`
      : '0 2px 12px rgba(10,22,40,0.06)'
    : recommended
      ? `0 0 34px ${accent}33, 0 20px 50px rgba(0,0,0,0.45)`
      : hovered
        ? `0 0 26px ${accent}22, 0 16px 40px rgba(0,0,0,0.40)`
        : 'none';

  return (
    <article
      className="relative flex flex-col rounded-2xl border overflow-hidden h-full"
      style={{
        background: cardBg,
        borderColor,
        boxShadow: shadow,
        transform: hovered ? 'translateY(-6px)' : recommended ? 'translateY(-4px)' : 'translateY(0)',
        transition:
          'transform 0.35s cubic-bezier(0.34,1.4,0.64,1), box-shadow 0.3s ease, border-color 0.3s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background: `linear-gradient(to right, ${accent}, ${accent}55, transparent)`,
          opacity: recommended || hovered ? 1 : 0.5,
        }}
      />

      <div className="flex flex-col flex-1 p-5">
        {/* Insignia */}
        {plan.badge ? (
          <div
            className="self-start inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full font-body mb-3 border"
            style={{
              backgroundColor: accent + '1f',
              borderColor: accent + '4d',
              color: isLight ? '#0e7490' : accent,
            }}
          >
            <span
              className="dental-pulse w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: accent }}
            />
            {plan.badge}
          </div>
        ) : (
          <div className="h-8 mb-3" aria-hidden="true" />
        )}

        {/* Nombre */}
        <h3
          className="font-heading text-2xl font-extrabold leading-none"
          style={{ color: isLight ? plan.accentInk : accent }}
        >
          {plan.name}
        </h3>

        {/* Descripción */}
        <p className="font-body text-xs mt-2 mb-4 leading-snug min-h-[2.5rem]" style={{ color: sub }}>
          {plan.description}
        </p>

        {/* Precio del término elegido */}
        <div className="flex items-end gap-1.5 flex-wrap">
          {plan.from && (
            <span className="font-body text-xs mb-1.5 font-semibold" style={{ color: sub }}>
              Desde
            </span>
          )}
          <span
            className="font-heading text-3xl md:text-[2rem] font-bold leading-none"
            style={{ color: ink }}
          >
            {price}
          </span>
          <span className="font-body text-xs mb-1.5" style={{ color: sub }}>
            {termText.unit}
          </span>
        </div>

        {/* Equivalencia mensual y ahorro del prepago */}
        <div className="min-h-[2.75rem] mt-1.5 flex flex-col items-start gap-1">
          {savings > 0 && (
            <>
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full font-body"
                style={{ backgroundColor: accent + '18', color: isLight ? plan.accentInk : accent }}
              >
                <i className="ri-price-tag-3-line text-[11px]" aria-hidden="true" />
                Ahorras {formatPrice(savings, currency)}/{termText.noun}
              </span>
              <span className="font-body text-[11px]" style={{ color: sub }}>
                equivale a {formatPrice(termMonthlyEquivalent(base, term), currency)}/mes
              </span>
            </>
          )}
        </div>

        {/* Implementación */}
        <div className="flex items-start gap-1.5 mt-2 mb-4">
          <i
            className="ri-add-circle-line text-xs flex-shrink-0 mt-0.5"
            style={{ color: accent }}
            aria-hidden="true"
          />
          <span className="font-body text-xs" style={{ color: sub }}>
            {plan.from ? 'Desde ' : ''}
            {implDiscounted && (
              <>
                <s style={{ color: isLight ? 'rgba(10,22,40,0.40)' : 'rgba(255,255,255,0.35)' }}>
                  {formatPrice(implFull, currency)}
                </s>{' '}
              </>
            )}
            <strong
              style={{
                color: implDiscounted
                  ? isLight
                    ? plan.accentInk
                    : accent
                  : isLight
                    ? 'rgba(10,22,40,0.78)'
                    : 'rgba(255,255,255,0.74)',
              }}
            >
              {formatPrice(implNow, currency)}
            </strong>{' '}
            implementación única
            {implDiscounted && (
              <span
                className="font-semibold"
                style={{ color: isLight ? plan.accentInk : accent }}
              >
                {' '}
                · 50% al pagar anual
              </span>
            )}
          </span>
        </div>

        {/* Capacidad */}
        <div
          className="rounded-xl p-3 mb-4"
          style={{ backgroundColor: accent + (isLight ? '0d' : '12'), border: `1px solid ${accent}22` }}
        >
          <p
            className="font-body text-[10px] font-bold uppercase tracking-wider mb-2"
            style={{ color: isLight ? 'rgba(10,22,40,0.72)' : accent }}
          >
            Capacidad incluida
          </p>
          <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {dentalCapacityMeta.map((meta) => {
              const value = plan.capacity[meta.field];
              return (
                <li key={meta.field} className="flex items-center gap-1.5">
                  <i
                    className={`${meta.icon} text-[11px] flex-shrink-0`}
                    style={{ color: accent }}
                    aria-hidden="true"
                  />
                  <span className="font-body text-[11px] leading-tight" style={{ color: sub }}>
                    <strong style={{ color: ink }}>{value}</strong>{' '}
                    {value === '1' ? meta.shortLabelSingular : meta.shortLabel}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="font-body text-[10px] mt-2" style={{ color: sub }}>
            <i className="ri-infinity-line text-[11px] mr-1" style={{ color: accent }} aria-hidden="true" />
            Pacientes y citas ilimitados
          </p>
        </div>

        {/* Funciones principales */}
        <ul className="flex flex-col gap-1.5 mb-4">
          {plan.highlights.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-2 font-body text-xs"
              style={{ color: isLight ? 'rgba(10,22,40,0.78)' : 'rgba(255,255,255,0.76)' }}
            >
              <i
                className="ri-checkbox-circle-fill flex-shrink-0 mt-[2px]"
                style={{ color: accent, fontSize: 13, lineHeight: 1 }}
                aria-hidden="true"
              />
              <span style={{ lineHeight: 1.45 }}>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="flex-1" />

        {/* CTA */}
        <button
          type="button"
          onClick={() => onSelectPlan(plan)}
          className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body hover:scale-[1.03] active:scale-[0.97]"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${plan.accentDark})`,
            color: '#ffffff',
            boxShadow: `0 6px 18px ${accent}30`,
          }}
        >
          {plan.ctaLabel}
          <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
        </button>

        {/* Ver todas las funciones */}
        <button
          type="button"
          onClick={onShowAll}
          aria-label={`Ver todas las funciones del plan ${plan.name}`}
          className="mt-2.5 w-full flex items-center justify-center gap-1.5 font-body text-xs font-semibold py-2 rounded-lg transition-colors cursor-pointer"
          style={{ color: isLight ? plan.accentInk : accent, backgroundColor: accent + '10' }}
        >
          <i className="ri-list-check-2 text-sm" aria-hidden="true" />
          Ver todas las funciones
        </button>

        {plan.footnote && (
          <p className="font-body text-[11px] leading-relaxed mt-3" style={{ color: sub }}>
            {plan.footnote}
          </p>
        )}
      </div>
    </article>
  );
}
